import React, { useEffect, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { resolveTabAccess } from '../auth/pageAccess';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import AdminNavTabs from '../components/AdminNavTabs';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';
const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

export default function AdminPosCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const statusRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [handoffUrl, setHandoffUrl] = useState('');

  const readStashed = () => {
    try {
      return {
        code: String(window.sessionStorage?.getItem('pos_connect_code') || '').trim(),
        state: String(window.sessionStorage?.getItem('pos_connect_state') || '').trim(),
        err: String(window.sessionStorage?.getItem('pos_connect_error') || '').trim(),
        merchantId: String(window.sessionStorage?.getItem('pos_connect_merchant_id') || '').trim(),
        clientId: String(window.sessionStorage?.getItem('pos_connect_client_id') || '').trim(),
        environment: String(window.sessionStorage?.getItem('pos_connect_environment') || '').trim(),
        provider: String(window.sessionStorage?.getItem('pos_connect_provider') || '').trim(),
      };
    } catch {
      return {
        code: '',
        state: '',
        err: '',
        merchantId: '',
        clientId: '',
        environment: '',
        provider: '',
      };
    }
  };

  const clearStashed = () => {
    try {
      window.sessionStorage?.removeItem('pos_connect_code');
      window.sessionStorage?.removeItem('pos_connect_state');
      window.sessionStorage?.removeItem('pos_connect_error');
      window.sessionStorage?.removeItem('pos_connect_merchant_id');
      window.sessionStorage?.removeItem('pos_connect_client_id');
      window.sessionStorage?.removeItem('pos_connect_environment');
      window.sessionStorage?.removeItem('pos_connect_provider');
    } catch {
    }
  };

  useEffect(() => {
    const run = async () => {
      try {
        setError('');
        setStatusMessage('');
        setHandoffUrl('');

        const stashed = readStashed();

        const code = String(params.get('code') || stashed.code || '').trim();
        const state = String(params.get('state') || stashed.state || '').trim();
        const merchantId = String(params.get('merchant_id') || params.get('merchantId') || stashed.merchantId || '').trim();
        const clientId = String(params.get('client_id') || params.get('clientId') || stashed.clientId || '').trim();

        try {
          const url = new URL(window.location.href);
          const hasCode = Boolean(url.searchParams.get('code'));
          const hasState = Boolean(url.searchParams.get('state'));
          if (hasCode || hasState) {
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            window.history.replaceState({}, document.title, `${url.pathname}${url.search ? url.search : ''}${url.hash || ''}`);
          }
        } catch {
        }

        let provider = String(params.get('provider') || stashed.provider || '').trim().toUpperCase();
        if (!provider && state && String(state).toLowerCase().startsWith('clover_')) provider = 'CLOVER';
        if (!provider && merchantId) provider = 'CLOVER';
        if (!provider) provider = 'SQUARE';
        let envName = String(params.get('environment') || stashed.environment || '').trim().toUpperCase();
        if (!envName && state) {
          try {
            const squareKey = `square_oauth_env_${state}`;
            const cloverKey = `clover_oauth_env_${state}`;
            const squareEnv = String(window.localStorage?.getItem(squareKey) || '').trim().toUpperCase();
            const cloverEnv = String(window.localStorage?.getItem(cloverKey) || '').trim().toUpperCase();
            if (cloverEnv) {
              provider = 'CLOVER';
              envName = cloverEnv;
            } else if (squareEnv) {
              provider = 'SQUARE';
              envName = squareEnv;
            }
          } catch {
          }
        }
        if (!envName) envName = 'SANDBOX';
        const err = String(params.get('error') || stashed.err || '').trim();

        if (err) {
          setError(err);
          return;
        }

        if (!code) {
          if (provider === 'CLOVER' && merchantId && clientId && API_BASE_URL) {
            try {
              const redirectUri = (() => {
                try {
                  return `${window.location.origin}/admin/channels/pos/callback`;
                } catch {
                  return '';
                }
              })();
              if (!redirectUri) {
                setError('Missing redirect URI for Clover connect.');
                return;
              }

              const session = await Auth.currentSession();
              const token = session.getIdToken().getJwtToken();

              const startRes = await fetch(`${API_BASE_URL}/admin/pos/connect/start`, {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ provider: 'CLOVER', environment: envName, redirect_uri: redirectUri }),
              });
              const startData = await startRes.json().catch(() => ({}));
              if (!startRes.ok) throw new Error(startData?.error || 'Failed to start Clover connect');

              const returnedState = String(startData?.state || '').trim();
              if (returnedState) {
                try {
                  window.localStorage?.setItem(`clover_oauth_env_${returnedState}`, String(envName || '').trim());
                } catch {
                }
              }

              const url = String(startData?.authorize_url || '').trim();
              if (!url) throw new Error('Connect started, but no authorization URL was returned.');
              setStatusMessage('Redirecting to Clover authorization…');
              window.location.assign(url);
              return;
            } catch (e) {
              setError(e?.message || 'Failed to start Clover connect');
              return;
            }
          }

          setError(`Missing code from ${provider === 'CLOVER' ? 'Clover' : 'Square'} callback.`);
          return;
        }

        const handoffCandidate = (() => {
          try {
            const qs = new URLSearchParams();
            qs.set('provider', provider);
            qs.set('environment', envName);
            qs.set('code', code);
            if (state) qs.set('state', state);
            if (merchantId) qs.set('merchant_id', merchantId);
            return `com.ezsellproducts.admin://pos/connect/callback?${qs.toString()}`;
          } catch {
            return '';
          }
        })();

        let token = '';
        try {
          const session = await Auth.currentSession();
          token = session.getIdToken().getJwtToken();
        } catch {
          if (handoffCandidate) setHandoffUrl(handoffCandidate);
          setError('Sign in to the web admin to finish, or open the EzSell Admin mobile app to complete POS connect.');
          return;
        }

        const res = await fetch(`${API_BASE_URL}/admin/pos/connect/callback`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ provider, code, state, environment: envName, merchant_id: merchantId }),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (handoffCandidate) setHandoffUrl(handoffCandidate);
          const base = String(data?.error || '').trim() || 'Failed to complete POS connect';
          const details = String(data?.details || '').trim();
          const extra = details ? ` (${details})` : '';
          throw new Error(`${base} [HTTP ${res.status}]${extra}`);
        }

        setStatusMessage(`${provider === 'CLOVER' ? 'Clover' : 'Square'} connected successfully.`);

        if (state) {
          try {
            window.localStorage?.removeItem(`square_oauth_env_${state}`);
            window.localStorage?.removeItem(`clover_oauth_env_${state}`);
          } catch {
          }
        }

        clearStashed();
      } catch (e) {
        const msg = e?.message || 'Failed to complete POS connect';
        setError(msg);
      } finally {
        setLoading(false);
        try {
          statusRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
          statusRef.current?.focus?.({ preventScroll: true });
        } catch {
          // ignore
        }
      }
    };

    run();
  }, [params]);

  useEffect(() => {
    const guard = async () => {
      const stashed = readStashed();
      const hasCallbackParams =
        Boolean(String(params.get('code') || '').trim()) ||
        Boolean(String(params.get('state') || '').trim()) ||
        Boolean(stashed.code) ||
        Boolean(stashed.state) ||
        Boolean(stashed.merchantId) ||
        Boolean(stashed.clientId) ||
        Boolean(stashed.err);

      if (hasCallbackParams) return;

      try {
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'General Settings');
        if (!access.allowed) {
          navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
        }
      } catch {
        navigate('/admin/login');
      }
    };

    guard();
  }, [navigate, params]);

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <h1>{PROJECT_NAME} Admin</h1>
        <p>POS Connect Callback</p>
      </header>

      <AdminNavTabs />

      <main className="dashboard-content">
        <section className="dashboard-section">
          <div ref={statusRef} tabIndex={-1} />
          {loading ? <p>Processing…</p> : null}
          {error ? <p className="error-message">{error}</p> : null}
          {statusMessage ? <p className="success-message">{statusMessage}</p> : null}
          {handoffUrl ? (
            <p style={{ marginTop: 12 }}>
              <a href={handoffUrl} className="primary-button" style={{ display: 'inline-block' }}>
                Open EzSell Admin app
              </a>
            </p>
          ) : null}

          <p>
            <Link to="/admin/channels/pos">Back to POS</Link>
          </p>
        </section>
      </main>
    </div>
  );
}
